import { useContext, useEffect, useState } from "react";

import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    MenuItem,
    TextField,
    Typography,
    Stack,
} from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import {
    ASSETS_UNDER_MANAGE,
    employmentArray,
    FIRM_TYPE,
    NUMBER_OF_EMPLOYEES,
    PRIMARY_CLIENT_SEGMENT,
    SPECIALTY_TYPE,
    YEARS_IN_BUSINESS,
} from "../AnnualConference/Registration/Dashboard/Employment/model";
import { ProgressContext } from "../Context/progressContext";
import { ShowNotification } from "../../utils/notification/snackbarUtils";
import { getCookie } from "../../utils/cookieHandle";
import { combineArrayElements } from "../../utils/array";
import { RequestNextApi } from "../../api/Request";

const generateArrayOfYears = () => {
    let max = new Date().getFullYear();
    let min = max - 125;
    let years = [];

    for (let i = max; i >= min; i--) {
        years.push(i);
    }
    return years;
};

// Helper function to try to parse a date string for the picker
const tryParseDate = (dateString) => {
    if (!dateString) return null;

    // Try to parse as MM/YYYY format
    if (typeof dateString === 'string' && dateString.includes('/') && dateString.split('/').length === 2) {
        try {
            const [month, year] = dateString.split('/');
            if (!isNaN(parseInt(month)) && !isNaN(parseInt(year))) {
                return dayjs().year(parseInt(year)).month(parseInt(month) - 1).date(1);
            }
        } catch {
        }
    }

    return null;
};

export default function Profile({ userId }) {
    const { update: updateProgress } = useContext(ProgressContext);
    const [showNonPartnerFields, setShowNonPartnerFields] = useState(false);

    const [prefChecked, setPrefChecked] = useState({
        cfpId: "",
        last4Social: "",
        imcaId: "",
        imcaZip: "",
        firmOwner: null,
        employment: "",
        specialty: "",
        numberOfEmployees: "",
        primaryClientSegment: "",
        firmType: "",
        assetsUnderManagement: "",
        yearStartedInIndustry: "",
        yearsInBusiness: "",
        linkedInProfileUrl: "",
        credentials: [],
        specialInterests: [],
        broker: "",
        crdId: "",
        organizationSummary: "",
        st_school: "",
        st_graduation: "",
        st_degree_of_interest: "",
        nameBadgeFirstName: "",
        nameBadgeLastName: "",
        nameBadgeFirmName: "",
        nameBadgeCityState: "",
    });

    const fetchSettings = async () => {
        updateProgress({ isLoading: true });
        try {
            const result = await RequestNextApi.post("/user/user-fetch2", {
                jwtToken: getCookie("jwt-token"),
                ...(userId ? { userId } : {}),
            });
            if (result.data.success) {
                // Use original graduation date value as is
                setPrefChecked({
                    cfpId: result.data.user.cfpId,
                    last4Social: result.data.user.last4Social,
                    imcaId: result.data.user.imcaId,
                    imcaZip: result.data.user.imcaZip,
                    firmOwner: result.data.user.firmOwner,
                    employment: result.data.user.employment,
                    specialty: result.data.user.specialty,
                    numberOfEmployees: result.data.user.numberOfEmployees,
                    primaryClientSegment: result.data.user.primaryClientSegment,
                    firmType: result.data.user.firmType,
                    assetsUnderManagement: result.data.user.assetsUnderManagement,
                    yearStartedInIndustry: result.data.user.yearStartedInIndustry,
                    yearsInBusiness: result.data.user.yearsInBusiness,
                    linkedInProfileUrl: result.data.user.linkedInProfileUrl,
                    credentials: result.data.user.credentials,
                    specialInterests: result.data.user.specialInterests,
                    broker: result.data.user.broker,
                    crdId: result.data.user.crdId,
                    organizationSummary: result.data.user.organizationSummary,
                    st_school: result.data.user.st_school,
                    st_graduation: result.data.user.st_graduation,
                    st_degree_of_interest: result.data.user.st_degree_of_interest,
                    nameBadgeFirstName: result.data.user.nameBadgeFirstName,
                    nameBadgeLastName: result.data.user.nameBadgeLastName,
                    nameBadgeFirmName: result.data.user.nameBadgeFirmName,
                    nameBadgeCityState: result.data.user.nameBadgeCityState,
                });
                setShowNonPartnerFields(!(result.data.user.partnerLevel > 0));
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (error) {
            ShowNotification.error("Fetching settings info failed.");
        }
        updateProgress({ isLoading: false });
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handler = (e) => {
        setPrefChecked({
            ...prefChecked,
            [e.target.name]: e.target.value,
        });
    };

    const handlerChecked = (e) => {
        setPrefChecked({
            ...prefChecked,
            [e.target.name]: e.target.checked,
        });
    };

    const handlerCredentials = (e) => {
        const credentials = prefChecked.credentials || [];
        if (e.target?.checked === true) {
            let newCredentials = credentials.slice(); // make a copy of the state array
            newCredentials.push(e.target?.name); // push a new element into the copy
            setPrefChecked({
                ...prefChecked,
                credentials: newCredentials,
            });
        } else if (e.target?.checked === false) {
            setPrefChecked({
                ...prefChecked,
                credentials: credentials.filter(
                    (credential) => credential !== e.target?.name
                ),
            });
        }
    };

    const handlerSpecialInterests = (e) => {
        const specialInterests = prefChecked.specialInterests || [];
        if (e.target?.checked === true) {
            let newSpecialty = specialInterests.slice(); // make a copy of the state array
            newSpecialty.push(e.target?.name); // push a new element into the copy
            setPrefChecked({
                ...prefChecked,
                specialInterests: newSpecialty,
            });
        } else if (e.target?.checked === false) {
            setPrefChecked({
                ...prefChecked,
                specialInterests: specialInterests.filter(
                    (item) => item !== e.target?.name
                ),
            });
        }
    };

    const handleClickSave = async () => {
        updateProgress({ isLoading: true });
        try {
            const result = await RequestNextApi.post("/user/user-save", {
                jwtToken: getCookie("jwt-token"),
                ...(userId ? { userId } : {}),
                cfpId: prefChecked.cfpId,
                last4Social: prefChecked.last4Social,
                imcaId: prefChecked.imcaId,
                imcaZip: prefChecked.imcaZip,
                firmOwner: prefChecked.firmOwner,
                employment: prefChecked.employment,
                specialty: prefChecked.specialty,
                numberOfEmployees: prefChecked.numberOfEmployees,
                primaryClientSegment: prefChecked.primaryClientSegment,
                firmType: prefChecked.firmType,
                assetsUnderManagement: prefChecked.assetsUnderManagement,
                yearStartedInIndustry: prefChecked.yearStartedInIndustry,
                yearsInBusiness: prefChecked.yearsInBusiness,
                linkedInProfileUrl: prefChecked.linkedInProfileUrl,
                credentials: combineArrayElements(prefChecked.credentials, "|"),
                specialInterests: combineArrayElements(
                    prefChecked.specialInterests,
                    "|"
                ),
                broker: prefChecked.broker,
                crdId: prefChecked.crdId,
                organizationSummary: prefChecked.organizationSummary,
                ...(userId ? {
                    nameBadgeFirstName: prefChecked.nameBadgeFirstName,
                    nameBadgeLastName: prefChecked.nameBadgeLastName,
                    nameBadgeFirmName: prefChecked.nameBadgeFirmName,
                    nameBadgeCityState: prefChecked.nameBadgeCityState,
                    st_school: prefChecked.st_school,
                    st_graduation: prefChecked.st_graduation,
                    st_degree_of_interest: prefChecked.st_degree_of_interest
                } : {}),
            });
            if (result.data.success) {
                ShowNotification.success(result.data.message);
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (error) {
            ShowNotification.error("Failed to save information.");
        }
        updateProgress({ isLoading: false });
    };

    return (
        <div className="gap-5 flex flex-col">
            {showNonPartnerFields && (
                <div
                    className={`bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6`}
                >
                    <div className="w-full lg:w-[280px]">
                        <Typography variant="h4" color="secondary">
                            CE Credit
                        </Typography>
                        <Divider className="my-2" />
                        <Typography variant="body2" color="primary.900">
                            Kingdom Advisors will directly report your CE credit to the{" "}
                            <Box component="span" fontWeight="fontWeightBold">
                                Certified Financial Planner Board of Standards
                            </Box>{" "}
                            and{" "}
                            <Box component="span" fontWeight="fontWeightBold">
                                Investments and Wealth Institute
                            </Box>
                        </Typography>
                    </div>
                    <div
                        className={`grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 flex-1`}
                    >
                        <TextField
                            label="CFP® ID#"
                            color="third"
                            fullWidth
                            size="small"
                            id="cfpId"
                            name="cfpId"
                            placeholder=""
                            focused
                            value={prefChecked.cfpId}
                            onChange={(e) => {
                                handler(e);
                            }}
                            helperText="Required if you desire CFP® CE Credit"
                        />
                        <TextField
                            label="Last 4 of SSN"
                            color="third"
                            fullWidth
                            size="small"
                            id="last4Social"
                            name="last4Social"
                            placeholder=""
                            focused
                            value={prefChecked.last4Social}
                            onChange={(e) => {
                                handler(e);
                            }}
                            helperText="Required if you desire CFP® CE Credit"
                        />
                        <TextField
                            label="IMCA/CIMA ID"
                            color="third"
                            fullWidth
                            size="small"
                            id="imcaId"
                            name="imcaId"
                            placeholder=""
                            focused
                            value={prefChecked.imcaId}
                            onChange={(e) => {
                                handler(e);
                            }}
                            helperText="Required if you desire IMCA/CIMA CE Credit"
                        />
                        <TextField
                            label="Zip/Postal Code"
                            color="third"
                            fullWidth
                            size="small"
                            id="imcaZip"
                            name="imcaZip"
                            placeholder=""
                            focused
                            value={prefChecked.imcaZip}
                            onChange={(e) => {
                                handler(e);
                            }}
                            helperText="Required if you desire IMCA/CIMA CE Credit"
                        />
                    </div>
                </div>
            )}

            {!!userId && <div
                className={`bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6`}
            >
                <div className="w-full lg:w-[280px]">
                    <Typography variant="h4" color="secondary">
                        Conference Name Badge
                    </Typography>
                </div>
                <div
                    className={`grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 flex-1`}
                >
                    <TextField
                        label="First Name"
                        color="third"
                        fullWidth
                        size="small"
                        id="nameBadgeFirstName"
                        name="nameBadgeFirstName"
                        placeholder=""
                        focused
                        value={prefChecked.nameBadgeFirstName}
                        onChange={(e) => {
                            handler(e);
                        }}
                    />
                    <TextField
                        label="Last Name"
                        color="third"
                        fullWidth
                        size="small"
                        id="nameBadgeLastName"
                        name="nameBadgeLastName"
                        placeholder=""
                        focused
                        value={prefChecked.nameBadgeLastName}
                        onChange={(e) => {
                            handler(e);
                        }}
                    />
                    <TextField
                        label="Firm Name"
                        color="third"
                        fullWidth
                        size="small"
                        id="nameBadgeFirmName"
                        name="nameBadgeFirmName"
                        placeholder=""
                        focused
                        value={prefChecked.nameBadgeFirmName}
                        onChange={(e) => {
                            handler(e);
                        }}
                    />
                    <TextField
                        label="City, State"
                        color="third"
                        fullWidth
                        size="small"
                        id="nameBadgeCityState"
                        name="nameBadgeCityState"
                        placeholder=""
                        focused
                        value={prefChecked.nameBadgeCityState}
                        onChange={(e) => {
                            handler(e);
                        }}
                    />
                </div>
            </div>}

            <div className={"flex flex-col gap-5"}>
                <div className="bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6">
                    <div className="w-full lg:w-[280px]">
                        <Typography variant="h4" color="secondary">
                            My Practice
                        </Typography>
                        <Divider className="my-2" />
                    </div>
                    <div className={`space-y-6 flex-1`}>
                        <TextField
                            label="Organizational Summary"
                            color="third"
                            fullWidth
                            multiline
                            rows={4}
                            id="organizationSummary"
                            name="organizationSummary"
                            placeholder=""
                            focused
                            value={prefChecked.organizationSummary}
                            onChange={(e) => {
                                handler(e);
                            }}
                            helperText="A brief description of your organization, products, and services."
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={prefChecked.firmOwner}
                                    name="firmOwner"
                                    onChange={(e) => {
                                        handlerChecked(e);
                                    }}
                                />
                            }
                            label="I am a firm owner"
                            className="flex-1"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                            <TextField
                                label="Employment"
                                color="third"
                                fullWidth
                                id="employment"
                                name="employment"
                                placeholder=""
                                focused
                                select
                                size="small"
                                value={prefChecked.employment}
                                // onChange={updateContextValue}
                                onChange={(e) => handler(e)}
                            >
                                {employmentArray.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Specialty"
                                color="third"
                                fullWidth
                                id="specialty"
                                name="specialty"
                                placeholder=""
                                focused
                                select
                                size="small"
                                value={prefChecked.specialty}
                                onChange={(e) => handler(e)}
                            >
                                {Object.values(SPECIALTY_TYPE).map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Number of employees"
                                color="third"
                                fullWidth
                                id="numberOfEmployees"
                                name="numberOfEmployees"
                                placeholder=""
                                focused
                                select
                                size="small"
                                value={prefChecked.numberOfEmployees}
                                onChange={(e) => handler(e)}
                            >
                                {Object.values(NUMBER_OF_EMPLOYEES).map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Primary client segment"
                                color="third"
                                fullWidth
                                id="primaryClientSegment"
                                name="primaryClientSegment"
                                placeholder=""
                                focused
                                select
                                size="small"
                                value={prefChecked.primaryClientSegment}
                                onChange={(e) => handler(e)}
                            >
                                {Object.values(PRIMARY_CLIENT_SEGMENT).map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Firm Type"
                                color="third"
                                fullWidth
                                id="firmType"
                                name="firmType"
                                placeholder=""
                                focused
                                select
                                size="small"
                                value={prefChecked.firmType}
                                onChange={(e) => handler(e)}
                            >
                                {Object.values(FIRM_TYPE).map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Assets under management"
                                color="third"
                                fullWidth
                                id="assetsUnderManagement"
                                name="assetsUnderManagement"
                                placeholder=""
                                focused
                                select
                                size="small"
                                value={prefChecked.assetsUnderManagement}
                                onChange={(e) => handler(e)}
                            >
                                {Object.values(ASSETS_UNDER_MANAGE).map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="In the financial services industry since"
                                color="third"
                                fullWidth
                                id="yearStartedInIndustry"
                                name="yearStartedInIndustry"
                                placeholder=""
                                focused
                                select
                                size="small"
                                value={prefChecked.yearStartedInIndustry}
                                onChange={(e) => handler(e)}
                            >
                                {Object.values(generateArrayOfYears()).map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Years in financial services industry"
                                color="third"
                                fullWidth
                                id="yearsInBusiness"
                                name="yearsInBusiness"
                                placeholder=""
                                focused
                                select
                                size="small"
                                value={prefChecked.yearsInBusiness}
                                onChange={(e) => handler(e)}
                            >
                                {Object.values(YEARS_IN_BUSINESS).map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                        <TextField
                            label="LinkedIn Profile URL"
                            color="third"
                            fullWidth
                            id="linkedInProfileUrl"
                            name="linkedInProfileUrl"
                            placeholder=""
                            focused
                            size="small"
                            value={prefChecked.linkedInProfileUrl}
                            onChange={(e) => handler(e)}
                            helperText="It should look like this https://linkedin.com/in/username"
                        />
                    </div>
                </div>

                <div className="bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6">
                    <div className="w-full lg:w-[280px]">
                        <Typography variant="h4" color="secondary">
                            Which of the following credentials do you hold?
                        </Typography>
                        <Divider className="my-2" />
                        <Typography variant="body2" color="primary.900">
                            Choose as many as applicable.
                        </Typography>
                    </div>
                    <div className={`space-y-6 flex-1`}>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-6">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("CKA®")}
                                        name="CKA®"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="CKA®"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("CFP®")}
                                        name="CFP®"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="CFP®"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("ChFC®")}
                                        // checked={firmOwner}
                                        name="ChFC®"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="ChFC®"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("CPA")}
                                        name="CPA"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="CPA"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("CPA/PFS")}
                                        // checked={firmOwner}
                                        name="CPA/PFS"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="CPA/PFS"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("CFA®")}
                                        // checked={firmOwner}
                                        name="CFA®"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="CFA®"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("CIMA")}
                                        // checked={firmOwner}
                                        name="CIMA"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="CIMA"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("AAMS®")}
                                        // checked={firmOwner}
                                        name="AAMS®"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="AAMS®"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("EA")}
                                        // checked={firmOwner}
                                        name="EA"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="EA"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("CLU®")}
                                        // checked={firmOwner}
                                        name="CLU®"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="CLU®"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("FIC")}
                                        // checked={firmOwner}
                                        name="FIC"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="FIC"
                                className="flex-1"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={prefChecked.credentials?.includes("JD")}
                                        // checked={firmOwner}
                                        name="JD"
                                        onChange={(e) => handlerCredentials(e)}
                                    />
                                }
                                label="JD"
                                className="flex-1"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6">
                    <div className="w-full lg:w-[280px]">
                        <Typography variant="h4" color="secondary">
                            Special Interests
                        </Typography>
                        <Divider className="my-2" />
                        <Typography variant="body2" color="primary.900">
                            Choose as many as applicable.
                        </Typography>
                    </div>
                    <div
                        className={`grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-6 flex-1`}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={prefChecked.specialInterests?.includes(
                                        "Comprehensive Financial Planning"
                                    )}
                                    // checked={firmOwner}
                                    name="Comprehensive Financial Planning"
                                    onChange={(e) => handlerSpecialInterests(e)}
                                />
                            }
                            label="Comprehensive Financial Planning"
                            className="flex-1"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={prefChecked.specialInterests?.includes(
                                        "Tax Planning"
                                    )}
                                    // checked={firmOwner}
                                    name="Tax Planning"
                                    onChange={(e) => handlerSpecialInterests(e)}
                                />
                            }
                            label="Tax Planning"
                            className="flex-1"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={prefChecked.specialInterests?.includes(
                                        "Estate Planning"
                                    )}
                                    // checked={firmOwner}
                                    name="Estate Planning"
                                    onChange={(e) => handlerSpecialInterests(e)}
                                />
                            }
                            label="Estate Planning"
                            className="flex-1"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={prefChecked.specialInterests?.includes(
                                        "Retirement Planning"
                                    )}
                                    // checked={firmOwner}
                                    name="Retirement Planning"
                                    onChange={(e) => handlerSpecialInterests(e)}
                                />
                            }
                            label="Retirement Planning"
                            className="flex-1"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={prefChecked.specialInterests?.includes(
                                        "Faith-Based Investing"
                                    )}
                                    // checked={firmOwner}
                                    name="Faith-Based Investing"
                                    onChange={(e) => handlerSpecialInterests(e)}
                                />
                            }
                            label="Faith-Based Investing"
                            className="flex-1"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={prefChecked.specialInterests?.includes(
                                        "Business Transition"
                                    )}
                                    // checked={firmOwner}
                                    name="Business Transition"
                                    onChange={(e) => handlerSpecialInterests(e)}
                                />
                            }
                            label="Business Transition"
                            className="flex-1"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={prefChecked.specialInterests?.includes(
                                        "Succession Planning"
                                    )}
                                    // checked={firmOwner}
                                    name="Succession Planning"
                                    onChange={(e) => handlerSpecialInterests(e)}
                                />
                            }
                            label="Succession Planning"
                            className="flex-1"
                        />
                    </div>
                </div>

                <div className="bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6">
                    <div className="w-full lg:w-[280px]">
                        <Typography variant="h4" color="secondary">
                            Investments
                        </Typography>
                    </div>
                    <div
                        className={`grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 flex-1`}
                    >
                        <TextField
                            label="Broker/Dealer Affiliation"
                            color="third"
                            fullWidth
                            size="small"
                            id="broker"
                            name="broker"
                            placeholder=""
                            focused
                            value={prefChecked.broker}
                            onChange={(e) => {
                                handler(e);
                            }}
                        />
                        <TextField
                            label="CRD #"
                            color="third"
                            fullWidth
                            size="small"
                            id="crdId"
                            name="crdId"
                            placeholder=""
                            focused
                            value={prefChecked.crdId}
                            onChange={(e) => {
                                handler(e);
                            }}
                        />
                    </div>
                </div>

                <div className="bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6">
                    <div className="w-full lg:w-[280px]">
                        <Typography variant="h4" color="secondary">
                            Continuing Education
                        </Typography>
                    </div>
                    <div
                        className={`grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 flex-1`}
                    >
                        <TextField
                            label="CFP ID#"
                            color="third"
                            fullWidth
                            size="small"
                            id="cfpId"
                            name="cfpId"
                            placeholder=""
                            focused
                            value={prefChecked.cfpId}
                            onChange={(e) => {
                                handler(e);
                            }}
                        />
                        <TextField
                            label="IWI ID#"
                            color="third"
                            fullWidth
                            size="small"
                            id="imcaId"
                            name="imcaId"
                            placeholder=""
                            focused
                            value={prefChecked.imcaId}
                            onChange={(e) => {
                                handler(e);
                            }}
                        />
                    </div>
                </div>
            </div>

            {!!userId && <div className="bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6">
                <div className="w-full lg:w-[280px]">
                    <Typography variant="h4" color="secondary">
                        Student
                    </Typography>
                    <Divider className="my-2" />
                </div>
                <div
                    className={`grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 flex-1`}
                >
                    <TextField
                        label="School"
                        color="third"
                        fullWidth
                        size="small"
                        id="st_school"
                        name="st_school"
                        placeholder=""
                        focused
                        value={prefChecked.st_school}
                        onChange={(e) => {
                            handler(e);
                        }}
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Expected Graduation Date"
                            value={tryParseDate(prefChecked.st_graduation)}
                            onChange={(newValue) => {
                                if (newValue) {
                                    const formattedDate = newValue.format('MM/YYYY');
                                    handler({ target: { name: 'st_graduation', value: formattedDate } });
                                }
                            }}
                            views={['month', 'year']}
                            openTo="month"
                            format="MM/YYYY"
                            slots={{
                                textField: (params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        focused
                                        color="third"
                                        inputProps={{
                                            ...params.inputProps,
                                            value: prefChecked.st_graduation || ''
                                        }}
                                        onChange={(e) => {
                                            handler(e);
                                            if (params.onChange) {
                                                params.onChange(e);
                                            }
                                        }}
                                        placeholder="MM/YYYY"
                                    />
                                )
                            }}
                        />
                    </LocalizationProvider>
                    <TextField
                        label="Interest"
                        color="third"
                        fullWidth
                        size="small"
                        id="st_degree_of_interest"
                        name="st_degree_of_interest"
                        placeholder=""
                        focused
                        value={prefChecked.st_degree_of_interest}
                        onChange={(e) => {
                            handler(e);
                        }}
                    />
                </div>
            </div>}

            <div>
                <Button variant="contained" color="secondary" onClick={handleClickSave}>
                    Save Profile Info
                </Button>
            </div>
        </div>
    );
}
